import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  canAutoReloadFor,
  fetchLatestBuildId,
  getCurrentBuildId,
  isNewerBuild,
  markAutoReloadedFor,
} from "@/shared/utils/app-version";

// 열려 있는 탭이 새 배포를 알아채는 주기. 탭으로 돌아올 때와 페이지를 옮길 때도 확인한다.
const CHECK_INTERVAL_MS = 5 * 60 * 1000;
// 포커스·이동이 연달아 일어나도 확인 요청은 1분에 한 번만.
const MIN_CHECK_GAP_MS = 60 * 1000;

/**
 * 새 버전이 배포됐는지 지켜보다가, 사용자가 **다른 페이지로 옮기는 순간** 전체 새로고침한다.
 *
 * 감지 즉시 새로고침하지 않는 이유 — 오더시트에 수량을 넣던 화면이 갑자기 날아가면 안 된다.
 * 페이지를 옮긴 직후라면 새 화면에는 아직 입력한 게 없어 안전하고, 사용자에게는 그냥 페이지가
 * 바뀐 것으로 보인다. 한 화면에 오래 머무는 경우는 배너(AppVersionBanner)로 알린다.
 */
export default function useAppVersionWatcher() {
  const [latestBuildId, setLatestBuildId] = useState<string | null>(null);
  const lastCheckedAt = useRef(0);
  const location = useLocation();
  const previousPathname = useRef(location.pathname);

  const check = useCallback(async () => {
    const current = getCurrentBuildId();
    if (!current) {
      return; // 로컬 개발 등 빌드 id 가 없는 번들
    }
    const now = Date.now();
    if (now - lastCheckedAt.current < MIN_CHECK_GAP_MS) {
      return;
    }
    lastCheckedAt.current = now;
    const latest = await fetchLatestBuildId();
    if (isNewerBuild(current, latest)) {
      setLatestBuildId(latest);
    }
  }, []);

  useEffect(() => {
    if (!getCurrentBuildId()) {
      return;
    }
    const timer = window.setInterval(check, CHECK_INTERVAL_MS);
    const onVisible = () => {
      if (document.visibilityState === "visible") {
        check();
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, [check]);

  useEffect(() => {
    // 검색 조건(쿼리스트링)만 바뀐 건 같은 화면이다 — 선택·입력 상태가 남아 있으니 건드리지 않는다.
    if (previousPathname.current === location.pathname) {
      return;
    }
    previousPathname.current = location.pathname;

    if (!latestBuildId) {
      check();
      return;
    }
    // 이동하면서 넘긴 state 는 새로고침하면 사라진다 — 그 화면이 state 에 기대고 있을 수 있다.
    if (location.state !== null && location.state !== undefined) {
      return;
    }
    if (!canAutoReloadFor(latestBuildId)) {
      return;
    }
    markAutoReloadedFor(latestBuildId);
    window.location.reload();
  }, [location.pathname, location.state, latestBuildId, check]);

  const reload = useCallback(() => {
    if (latestBuildId) {
      markAutoReloadedFor(latestBuildId);
    }
    window.location.reload();
  }, [latestBuildId]);

  return { hasNewVersion: latestBuildId !== null, reload };
}
